/**
 * PenalesGame.js v5 — Easter eggs: patrón 18, El Analista, Penal Potenciado
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import Phaser from 'phaser';
import PenalesScene from './penalesScene';
import {
  getZonasDisponibles, getDificultad, getDificultadLabel,
  ejecutarDisparo, leerMejorRacha, guardarMejorRacha,
  resetPatron, verificarAnalista,
} from './penalesLogic';
import './penalesStyles.css';

const crearSonidoGol = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const bufferSize = ctx.sampleRate * 0.18;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++)
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 1.2);
    const src = ctx.createBufferSource(); src.buffer = buffer;
    const filter = ctx.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 800;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
    src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
    src.start(); src.stop(ctx.currentTime + 0.2);
  } catch(e) {}
};

const crearSonidoAtajada = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(220, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(110, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(); osc.stop(ctx.currentTime + 0.2);
  } catch(e) {}
};

const crearSonidoPotenciado = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(440, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.28);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(); osc.stop(ctx.currentTime + 0.3);
  } catch(e) {}
};

const PenalesGame = ({ session }) => {
  const gameRef  = useRef(null);
  const phaserRef = useRef(null);
  const sceneRef  = useRef(null);
  const stateRef  = useRef({ goals: 0, shots: 0, streak: 0, shooting: false, gameOver: false, transitioning: false });

  const [loaded,        setLoaded]        = useState(false);
  const [message,       setMessage]       = useState(null);
  const [gameOver,      setGameOver]      = useState(false);
  const [goals,         setGoals]         = useState(0);
  const [shots,         setShots]         = useState(0);
  const [streak,        setStreak]        = useState(0);
  const [bestStreak,    setBestStreak]    = useState(0);
  const [nuevoRecord,   setNuevoRecord]   = useState(false);
  const [finalStreak,   setFinalStreak]   = useState(0);
  const [primerPenal,   setPrimerPenal]   = useState(true);

  // Easter egg: Penal Potenciado
  const [potenciadoDisp,   setPotenciadoDisp]   = useState(true);
  const [potenciadoActivo, setPotenciadoActivo] = useState(0);
  const [showPotenciado,   setShowPotenciado]   = useState(false);

  // Easter egg: El Analista
  const golZonasRef = useRef([]);
  const potActivoRef = useRef(0);

  useEffect(() => {
    potActivoRef.current = potenciadoActivo;
  }, [potenciadoActivo]);

  useEffect(() => {
    if (session?.user?.id) leerMejorRacha(session.user.id).then(r => setBestStreak(r));
    resetPatron();
  }, [session]);

  // Tecla + activa Penal Potenciado
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === '+' || e.key === '=') && potenciadoDisp && !gameOver && loaded) {
        setPotenciadoDisp(false);
        setPotenciadoActivo(2);
        setShowPotenciado(true);
        crearSonidoPotenciado();
        setTimeout(() => setShowPotenciado(false), 2500);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [potenciadoDisp, gameOver, loaded]);

  useEffect(() => {
    if (!gameRef.current) return;
    const timer = setTimeout(() => {
      const game = new Phaser.Game({
        type: Phaser.AUTO, width: 860, height: 480,
        backgroundColor: '#020b18', parent: gameRef.current, scene: PenalesScene,
        scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_HORIZONTALLY },
      });
      phaserRef.current = game;
      game.events.once('ready', () => {
        const scene = game.scene.getScene('PenalesScene');
        sceneRef.current = scene;
        scene.onZonaClick = (id) => handleZonaClick(id);
        setLoaded(true);
      });
    }, 1500);
    return () => {
      clearTimeout(timer);
      if (phaserRef.current) { phaserRef.current.destroy(true); phaserRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sceneRef.current) sceneRef.current.onZonaClick = handleZonaClick;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, potenciadoActivo]);

  const handleZonaClick = useCallback((zonaId) => {
    if (stateRef.current.shooting || stateRef.current.gameOver || stateRef.current.transitioning) return;
    stateRef.current.shooting = true;
    setMessage(null);
    setPrimerPenal(false);

    const { goals: g, shots: s, streak: st } = stateRef.current;
    const zonasDisponibles = getZonasDisponibles(st);
    const esPotenciado = potActivoRef.current > 0;

    const { gol, zonaArqueroId } = ejecutarDisparo(zonaId, zonasDisponibles, s, esPotenciado);
    if (esPotenciado) setPotenciadoActivo(p => Math.max(0, p - 1));

    const newShots  = s + 1;
    const newStreak = gol ? st + 1 : 0;
    const newGoals  = gol ? g + 1  : g;

    stateRef.current = { goals: newGoals, shots: newShots, streak: newStreak, shooting: true, gameOver: false };
    if (sceneRef.current) sceneRef.current.setZonasActivas(getZonasDisponibles(newStreak).map(z => z.id));

    if (sceneRef.current) {
      sceneRef.current.animarDisparo(zonaId, zonaArqueroId, gol, async () => {
        stateRef.current.shooting = false;
        setGoals(newGoals);
        setShots(newShots);
        setStreak(newStreak);

        if (gol) {
          golZonasRef.current = [...golZonasRef.current, zonaId];

          const subMsg = esPotenciado
            ? ' NO PODÍA SER ATAJADO'
            : newStreak >= 5 ? ` Racha ${newStreak}` : '';
          setMessage({ text: '¡GOL!', sub: subMsg, type: 'goal' });
          setTimeout(() => setMessage(null), 1100);

          if (session?.user?.id) {
            guardarMejorRacha(session.user.id, newStreak).then(ok => {
              if (ok) { setBestStreak(newStreak); setNuevoRecord(true); setTimeout(() => setNuevoRecord(false), 2200); }
            });
            if (golZonasRef.current.length >= 10) {
              verificarAnalista(session.user.id, golZonasRef.current);
            }
          }
        } else {
          stateRef.current.gameOver = true;
          stateRef.current.transitioning = true;
          if (sceneRef.current) sceneRef.current.gameOver = true;
          setFinalStreak(st);
          setMessage({ text: 'ATAJADO', sub: '', type: 'saved' });
          if (st > 0 && session?.user?.id) guardarMejorRacha(session.user.id, st).then(ok => { if (ok) setBestStreak(st); });
          golZonasRef.current = [];
          setTimeout(() => {
            setMessage(null);
            stateRef.current.transitioning = false;
            setGameOver(true);
          }, 950);
        }
      }, () => {
        if (gol) crearSonidoGol();
        else crearSonidoAtajada();
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const reiniciar = () => {
    stateRef.current = { goals: 0, shots: 0, streak: 0, shooting: false, gameOver: false, transitioning: false };
    if (sceneRef.current) { sceneRef.current.gameOver = false; sceneRef.current.shooting = false; }
    setGoals(0); setShots(0); setStreak(0);
    setGameOver(false); setMessage(null); setNuevoRecord(false);
    setPrimerPenal(true); setPotenciadoActivo(0);
    golZonasRef.current = [];
    resetPatron();
    if (sceneRef.current) sceneRef.current.setZonasActivas([0, 1, 2, 3, 4, 5]);
  };

  const dificultad    = getDificultad(streak);
  const dificultadLbl = getDificultadLabel(streak);

  return (
    <div className="penales-wrapper">
      {!loaded && (
        <div className="penales-loading">
          <div className="penales-loading-bg" />
          <h1 className="penales-loading-title">PENALES</h1>
          <p className="penales-loading-text">Cargando minijuego...</p>
          <div className="penales-loading-bar-wrap"><div className="penales-loading-bar" /></div>
        </div>
      )}

      <div className="penales-scoreboard" style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.5s' }}>
        <div className="penales-sb-block">
          <span className="penales-sb-label">Penales</span>
          <span className="penales-sb-value">{goals}</span>
        </div>
        <div className="penales-sb-title"> PENALES</div>
        <div className="penales-sb-block">
          <span className="penales-sb-label">Récord</span>
          <span className="penales-sb-value orange">
            {Math.max(bestStreak, streak)}
            {nuevoRecord && <span style={{ fontSize: '0.85rem', marginLeft: '4px', color: '#00ff88' }}>★</span>}
          </span>
        </div>
        <div className="penales-sb-block">
          <span className={`penales-difficulty ${dificultad}`}>{dificultadLbl}</span>
        </div>
      </div>

      {/* Banner Penal Potenciado */}
      {showPotenciado && (
        <div style={{ textAlign: 'center', padding: '4px 0', fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.4rem', color: '#ffd700', letterSpacing: '4px', textShadow: '0 0 20px #ffd70099' }}>
           ESTE PENAL NO PUEDE SER ATAJADO × 2
        </div>
      )}
      {potenciadoActivo > 0 && !showPotenciado && (
        <div style={{ textAlign: 'center', padding: '2px 0', fontFamily: "'Share Tech Mono', monospace", fontSize: '0.8rem', color: '#ffd700', letterSpacing: '2px' }}>
           Este penal no puede ser atajado{potenciadoActivo > 1 ? ' (×2)' : ''}
        </div>
      )}

      <div style={{
        textAlign: 'center', padding: '6px 0',
        fontFamily: "'Share Tech Mono', monospace", fontSize: '0.92rem', color: '#00f2ff',
        letterSpacing: '2.5px', textShadow: '0 0 12px rgba(0,242,255,0.6)',
        animation: 'parpadeaInstruccion 2.5s ease-in-out infinite',
        visibility: loaded && primerPenal && !gameOver ? 'visible' : 'hidden',
      }}>
        HACÉ CLIC EN UNA ZONA DEL ARCO PARA DISPARAR
      </div>

      <div className="penales-game-area" style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.5s 0.3s', position: 'relative' }}>
        <div className="penales-canvas-wrap" ref={gameRef} />
        {message && (
          <div className="penales-message">
            <div className={`penales-message-text ${message.type}`}>{message.text}</div>
            {message.sub && <div className="penales-message-sub">{message.sub}</div>}
          </div>
        )}
      </div>

      {gameOver && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(2,8,20,0.92)', backdropFilter: 'blur(4px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', zIndex: 9999, overflowY: 'auto', padding: '20px', boxSizing: 'border-box', animation: 'fadeInGO 0.35s ease-out' }}>
          <style>{`@keyframes fadeInGO { from { opacity:0; transform:scale(0.94); } to { opacity:1; transform:scale(1); } }`}</style>
          <img src="/atajado.png" alt="Arquero" style={{ width: 'min(380px, 70vw)', objectFit: 'contain', filter: 'drop-shadow(0 0 20px rgba(255,51,68,0.5))' }} />
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(2.2rem,6vw,3.8rem)', letterSpacing: '6px', lineHeight: 1, color: '#ff3344', textShadow: '0 0 24px rgba(255,51,68,0.7), 0 4px 0 rgba(100,0,0,0.8)' }}>ATAJADO</div>
          <div style={{ color: '#475569', fontFamily: "'Share Tech Mono', monospace", fontSize: '0.68rem', letterSpacing: '3px' }}>EL ARQUERO TE GANÓ</div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
            {[{ label: 'PENALES', val: goals, col: '#00f2ff' }, { label: 'RÉCORD', val: Math.max(bestStreak, finalStreak), col: '#ff8c00' }].map(({ label, val, col }) => (
              <div key={label} style={{ textAlign: 'center', background: '#0a1828', border: '1px solid #1e293b', borderRadius: '10px', padding: '10px 18px' }}>
                <p style={{ color: '#475569', fontFamily: "'Share Tech Mono', monospace", fontSize: '0.52rem', letterSpacing: '2px', margin: '0 0 4px' }}>{label}</p>
                <p style={{ color: col, fontFamily: "'Bebas Neue', sans-serif", fontSize: '2.2rem', margin: 0, lineHeight: 1 }}>{val}</p>
              </div>
            ))}
          </div>
          {finalStreak > bestStreak && (
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.05rem', letterSpacing: '3px', color: '#ff8c00', padding: '5px 16px', background: 'rgba(255,140,0,0.10)', border: '1px solid rgba(255,140,0,0.28)', borderRadius: '6px' }}>★ NUEVO RÉCORD ★</div>
          )}
          <button onClick={reiniciar} style={{ marginTop: '6px', fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(1rem,3vw,1.25rem)', letterSpacing: '4px', padding: '12px 36px', background: '#00ff88', color: '#020b18', border: 'none', borderRadius: '6px', cursor: 'pointer', boxShadow: '0 0 22px rgba(0,255,136,0.4)', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            VOLVER A JUGAR
          </button>
        </div>
      )}
    </div>
  );
};

export default PenalesGame;